import { ArrowUp, ArrowDown, Plus, X } from "lucide-react";
import { FIELD_CLASS, LABEL_CLASS } from "./shared";

// Long-form "Why It's Different" content sections — heading + paragraph + optional sub-bullets,
// reorderable. Only shown for existing products.
export const ContentSectionsSection = ({
  isNew, form, addContentSection, removeContentSection, updateContentSection, moveContentSection,
  addSectionBullet, removeSectionBullet, updateSectionBullet,
}) => {
  if (isNew) return null;
  return (
    <div>
      <span className={LABEL_CLASS}>Why It's Different (long-form content sections)</span>
      <p className="text-white/40 text-xs mt-1 mb-3">
        For premium products that need real justification beyond a bullet list — each section gets its own
        heading, a paragraph, and an optional sub-bullet list (e.g. materials, engineering, what's included).
        Rendered as a dedicated section on the product page below Key Features. Leave empty to skip it.
      </p>
      <div className="space-y-4">
        {form.contentSections.map((section, si) => (
          <div key={si} data-testid={`admin-content-section-${si}`} className="border border-[var(--border-default)] bg-[var(--bg-secondary)]/40 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                data-testid={`admin-content-section-heading-${si}`}
                value={section.heading}
                onChange={(e) => updateContentSection(si, "heading", e.target.value)}
                className={FIELD_CLASS + " mt-0 flex-1"}
                placeholder="Section heading, e.g. Engineering That Redefines Freestanding"
              />
              <button type="button" onClick={() => moveContentSection(si, -1)} disabled={si === 0} data-testid={`admin-content-section-up-${si}`} className="text-white/50 hover:text-[var(--brand-primary)] disabled:opacity-30 shrink-0">
                <ArrowUp size={16} />
              </button>
              <button type="button" onClick={() => moveContentSection(si, 1)} disabled={si === form.contentSections.length - 1} data-testid={`admin-content-section-down-${si}`} className="text-white/50 hover:text-[var(--brand-primary)] disabled:opacity-30 shrink-0">
                <ArrowDown size={16} />
              </button>
              <button type="button" onClick={() => removeContentSection(si)} data-testid={`admin-content-section-remove-${si}`} className="text-white/50 hover:text-red-400 shrink-0">
                <X size={16} />
              </button>
            </div>
            <textarea
              data-testid={`admin-content-section-body-${si}`}
              rows={4}
              value={section.body}
              onChange={(e) => updateContentSection(si, "body", e.target.value)}
              className={FIELD_CLASS + " mt-0"}
              placeholder="Paragraph copy for this section…"
            />
            <div className="space-y-2 pl-2 border-l border-[var(--border-default)]">
              {section.bullets.map((b, bi) => (
                <div key={bi} data-testid={`admin-content-section-bullet-${si}-${bi}`} className="flex items-center gap-2 pl-3">
                  <input
                    value={b}
                    onChange={(e) => updateSectionBullet(si, bi, e.target.value)}
                    className={FIELD_CLASS + " mt-0 flex-1"}
                    placeholder="Sub-bullet point"
                  />
                  <button type="button" onClick={() => removeSectionBullet(si, bi)} data-testid={`admin-content-section-bullet-remove-${si}-${bi}`} className="text-white/50 hover:text-red-400 shrink-0">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addSectionBullet(si)} data-testid={`admin-content-section-bullet-add-${si}`} className="pl-3 text-xs font-mono text-white/50 hover:text-[var(--brand-primary)] underline">
                + Add Sub-Bullet
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer pt-1">
              <input
                type="checkbox"
                data-testid={`admin-content-section-highlight-${si}`}
                checked={!!section.highlight}
                onChange={(e) => updateContentSection(si, "highlight", e.target.checked)}
                className="accent-[var(--brand-primary)]"
              />
              Highlight this section (full-width gold callout — use for the one key takeaway)
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid={`admin-content-section-linktext-${si}`}
                value={section.linkText || ""}
                onChange={(e) => updateContentSection(si, "linkText", e.target.value)}
                className={FIELD_CLASS + " mt-0"}
                placeholder="Optional link text, e.g. Shop Freestanding Awnings"
              />
              <input
                data-testid={`admin-content-section-linkhref-${si}`}
                value={section.linkHref || ""}
                onChange={(e) => updateContentSection(si, "linkHref", e.target.value)}
                className={FIELD_CLASS + " mt-0"}
                placeholder="Optional link URL, e.g. /awnings"
              />
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={addContentSection} data-testid="admin-content-section-add" className="btn-secondary mt-3">
        <Plus size={14} /> Add Section
      </button>
    </div>
  );
};
